import React, { useContext, useEffect } from 'react';
import { Box } from '@mui/material';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { SizeMe } from 'react-sizeme';
import classNames from 'classnames';
import { FullScreenTrackingContext } from '../../../../Rooms/Tracking/FullScreenTrackingContext';
import { PDFViewerProps } from '../PDFViewerProps';
import PSPDFKitInternalViewer from './PSPDFKitInternalViewer';
import usePDFViewerStyles from '../../usePDFViewerStyles';

/**
 * Mandatory wrapper for PSPDFKitInternalViewer to pass the SizeMe.size.width
 * as a prop so it can use it to compute the scale and dimensions of the
 * actual viewer and to grant it full screen capabilities without affecting
 * the mounting/initialization of the container node
 */
const PSPDFKitViewer = React.forwardRef<HTMLDivElement, PDFViewerProps>(
  (props: PDFViewerProps, ref): JSX.Element => {
    const pdfViewerClasses = usePDFViewerStyles();

    const { setFullScreenContentId } = useContext(FullScreenTrackingContext);

    const fullScreenHandle = useFullScreenHandle();

    const {
      contentId,
      darkTheme,
      downloadable,
      downloadTrigger,
      onDownload,
      rounded,
    } = props;

    useEffect(() => {
      if (downloadable && downloadTrigger && onDownload) {
        onDownload();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadable, downloadTrigger]);

    const onFullScreenChange = (isFullScreenActive: boolean) => {
      setFullScreenContentId(isFullScreenActive ? contentId : null);
    };

    return (
      <Box
        className={classNames({
          [pdfViewerClasses.viewerRounded]: rounded,
          [pdfViewerClasses.viewerWithBorder]: !darkTheme,
        })}
        ref={ref}
      >
        <FullScreen onChange={onFullScreenChange} handle={fullScreenHandle}>
          <SizeMe refreshMode="debounce">
            {({ size }) => (
              <PSPDFKitInternalViewer
                fullScreenHandle={fullScreenHandle}
                width={size.width || undefined}
                {...props}
              />
            )}
          </SizeMe>
        </FullScreen>
      </Box>
    );
  }
);

export default PSPDFKitViewer;
